import { Router } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { TranslateModule } from "@ngx-translate/core";
import { startOfYear, subYears } from "date-fns";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Component, computed, effect, inject, Injector, runInInjectionContext, Signal, signal, ViewEncapsulation } from "@angular/core";

import {
  ApplicationUserTypeId,
  AuthService,
  customValidations,
  Dropdown,
  EmploymentType,
  LookupsService,
  mustMatch,
  strongPassword,
  fallbackOnError,
} from "src/app/core";
import { environment } from "src/environments/environment";
import { InputTextComponent, ButtonComponent, DatepickerComponent, DropdownComponent, InputTextDropdownComponent } from "src/app/shared";

@Component({
  selector: "app-register",
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    InputTextComponent,
    ButtonComponent,
    DatepickerComponent,
    DropdownComponent,
    InputTextDropdownComponent,
  ],
  templateUrl: "./register.component.html",
  styleUrl: "./register.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class RegisterComponent {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private injector = inject(Injector);
  private authService = inject(AuthService);
  private lookupsService = inject(LookupsService);

  registerForm!: FormGroup;
  showPassword = false;
  birthDateMaxDate = startOfYear(subYears(new Date(), environment.minAge));

  genders = toSignal(this.lookupsService.getGenders(), { initialValue: [] });
  countries = toSignal(this.lookupsService.getCountries().pipe(fallbackOnError([])), { initialValue: [] });
  governorates: Signal<Dropdown[] | undefined> = signal([]);
  states: Signal<Dropdown[] | undefined> = signal([]);

  currentPhoneCountryCode = computed(() => {
    return "+968";

    // const countryCode = window.navigator.language.split("-").pop();
    // const country = this.countries().find((c) => c.code === countryCode);

    // if (!country) return null;
    // return country.phoneCode as string;
  });

  get phoneCountryCode() {
    if (!this.countries().length && !this.registerForm.get("countryCode")?.value) return null;

    const country = this.countries().find((c) => c.phoneCode === this.registerForm.get("countryCode")?.value);

    if (!country) return null;
    return country.code;
  }

  constructor() {
    effect(() => {
      if (this.countries().length) {
        this.registerForm.get("countryCode")?.setValue(this.currentPhoneCountryCode());
        this.getPhoneCountryCode(this.currentPhoneCountryCode()!);
      }
    });
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    this.initForm();
  }

  initForm() {
    this.registerForm = this.fb.group(
      {
        nameEn: [null, [Validators.required, customValidations.englishCharactersOnly()]],
        nameAr: [null, [customValidations.arabicCharactersOnly()]],
        email: [null, [Validators.required, customValidations.email()]],
        phone: [null, [Validators.required]],
        countryCode: this.fb.control(null, [Validators.required]),
        gender: [null],
        dateOfBirth: [null],
        userType: [ApplicationUserTypeId.Client],
        employmentType: [EmploymentType.Employee],
        isStudent: [false],
        isOmanResident: [false],

        countryId: [null, [Validators.required]],
        governorateId: [null, [Validators.required]],
        stateId: [null, [Validators.required]],

        password: [null, [Validators.required, Validators.minLength(8), strongPassword]],
        confirmPassword: [null, [Validators.required, Validators.minLength(8), strongPassword]],
      },
      {
        validators: mustMatch("password", "confirmPassword"),
      },
    );
  }

  getGovernorates(id: number) {
    const isOman = this.countries()
      .find((c) => c.id === id)
      ?.code.toLowerCase()
      .includes("om");

    if (isOman) {
      runInInjectionContext(
        this.injector,
        () => (this.governorates = toSignal(this.lookupsService.getGovernorates({ id }).pipe(fallbackOnError(undefined)))),
      );
      this.registerForm.get("governorateId")!.enable();
      this.registerForm.get("stateId")!.enable();
      this.registerForm.patchValue({ isOmanResident: true });
    } else {
      this.registerForm.get("governorateId")!.disable();
      this.registerForm.get("stateId")!.disable();
      this.registerForm.patchValue({ isOmanResident: false });
    }
  }

  getStates(id: number) {
    runInInjectionContext(
      this.injector,
      () => (this.states = toSignal(this.lookupsService.getStates({ id }).pipe(fallbackOnError(undefined)))),
    );
  }

  getPhoneCountryCode(countryCode: string | number | boolean) {
    const country = this.countries().find((c) => c.phoneCode === countryCode);

    if (country) {
      const countryPhoneRegex = (country as any).phoneCodeRegex;

      this.registerForm
        .get("phone")
        ?.setValidators([Validators.required, customValidations.withCustomMobileNumberRegex(countryPhoneRegex)]);
    }
  }

  register() {
    const formValue = this.registerForm.value;

    if (!formValue.nameAr) formValue.nameAr = formValue.nameEn;

    this.authService.register(formValue).subscribe({
      next: (data) => {
        localStorage.setItem("username", formValue.email);
        localStorage.setItem("phone", formValue.phone);

        if (data["code"] === 1) {
          this.router.navigateByUrl("/auth/verify");
        } else {
          this.router.navigateByUrl("/auth/login");
        }
      },
    });
  }
}
